import { useEffect, useState } from 'react';
import { techdocsPlugin } from '@backstage/plugin-techdocs';
import {
  createTechDocsAddonExtension,
  TechDocsAddonLocations,
  useTechDocsReaderPage,
} from '@backstage/plugin-techdocs-react';
import { useRememberDoc } from '../components/home/useRecentDocs';

/**
 * The page currently rendered in the reader's shadow root. The reader swaps
 * in a new element for every page that loads and keeps the previous one when
 * a page fails, so each change of this value is one successful page visit.
 */
function useRenderedPage(shadowRoot: ShadowRoot | undefined): Node | null {
  const [page, setPage] = useState<Node | null>(null);
  useEffect(() => {
    if (!shadowRoot) return undefined;
    setPage(shadowRoot.firstChild);
    const observer = new MutationObserver(() => setPage(shadowRoot.firstChild));
    observer.observe(shadowRoot, { childList: true });
    return () => observer.disconnect();
  }, [shadowRoot]);
  return page;
}

/**
 * Records the entity whose docs are open in the homepage's "Recently viewed"
 * history, once per successfully rendered page. The history keeps one entry
 * per entity, so reading more of the same docs refreshes that entry's time
 * rather than adding pages. Pages that fail to load are not recorded.
 */
function RecentDocsTrackerAddon(): null {
  const { entityRef, shadowRoot } = useTechDocsReaderPage();
  const { namespace, kind, name } = entityRef;
  const remember = useRememberDoc();
  const page = useRenderedPage(shadowRoot);

  useEffect(() => {
    if (page) remember({ namespace, kind, name });
  }, [page, namespace, kind, name, remember]);

  return null;
}

// Content, not Header: the catalog's Docs tab renders no header, and content
// addons mount only once a page has rendered. Renders nothing either way.
export const RecentDocsTracker = techdocsPlugin.provide(
  createTechDocsAddonExtension({
    name: 'RecentDocsTracker',
    location: TechDocsAddonLocations.Content,
    component: RecentDocsTrackerAddon,
  }),
);
